import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// Create axios instance
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/',
  timeout: 30000, // Increased from 10000 to 30000 (30 seconds) for large responses
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store active requests for cancellation
const activeRequests = new Map();

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('pharmaman-user-store');
    if (token) {
      try {
        const parsedToken = JSON.parse(token);
        if (parsedToken.state?.token) {
          config.headers.Authorization = `Token ${parsedToken.state.token}`;
        }
      } catch (error) {
        console.error('Error parsing token from localStorage:', error);
      }
    }
    
    // For OTP endpoints, don't add any token
    if (config.url?.includes('/send-otp/') || config.url?.includes('/verify-otp/')) {
      // Still need to handle abort signal if provided
      if (!config.signal && !config.cancelToken) {
        const abortController = new AbortController();
        config.signal = abortController.signal;
        const requestKey = `${config.method}_${config.url}`;
        activeRequests.set(requestKey, abortController);
      }
      return config;
    }
    
    // Create AbortController for this request if not already provided
    if (!config.signal && !config.cancelToken) {
      const abortController = new AbortController();
      config.signal = abortController.signal;
      
      // Store the controller for potential cancellation
      const requestKey = `${config.method}_${config.url}`;
      activeRequests.set(requestKey, abortController);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Clean up request from active requests map
    if (response.config) {
      const requestKey = `${response.config.method}_${response.config.url}`;
      activeRequests.delete(requestKey);
    }
    return response;
  },
  (error) => {
    // Clean up request from active requests map
    if (error.config) {
      const requestKey = `${error.config.method}_${error.config.url}`;
      activeRequests.delete(requestKey);
    }
    
    // Handle network errors (including aborted requests)
    if (error.code === 'ECONNABORTED' || error.name === 'AbortError' || error.message === 'canceled') {
      // Don't log canceled requests as errors
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      // Handle unauthorized access
      console.log('Unauthorized access, redirecting to login');
      // You can dispatch a logout action here if needed
    }
    
    return Promise.reject(error);
  }
);

// Utility function to cancel all active requests
export const cancelAllRequests = () => {
  activeRequests.forEach((controller, key) => {
    controller.abort();
    activeRequests.delete(key);
  });
};

// Utility function to cancel a specific request
export const cancelRequest = (url, method = 'get') => {
  const requestKey = `${method.toUpperCase()}_${url}`;
  const controller = activeRequests.get(requestKey);
  if (controller) {
    controller.abort();
    activeRequests.delete(requestKey);
  }
};

// Utility function to check if running on native platform
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

// Utility function to get platform info
export const getPlatformInfo = async () => {
  if (isNativePlatform()) {
    const { App } = await import('@capacitor/app');
    return await App.getInfo();
  }
  return null;
};

export default apiClient;
